import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { TableDescriptor, ToolbarButton } from '../../components/kiene-table/table-descriptor';
import { MatDialog } from '@angular/material/dialog';
import { KieneTableService } from '../../components/kiene-table/kiene-table.service';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { FileUploadService } from '../../dialogs/file-upload-dialog/file-upload.service';
import {
    KieneBooleanTableColumn,
    NumberTableColumn,
    PdfTableColumn,
    TableColumn,
} from '../../components/kiene-table/table-column';
import { Condition, Operator, TableOption } from '../../components/kiene-table/table-option';
import { ConfirmDeleteDialogComponent } from '../../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { Gutschrift } from './gutschrift';
import { AddGutschriftDialogComponent } from './add-gutschrift-dialog/add-gutschrift-dialog.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { ConfirmDialogComponent } from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog.component';
import {
    ConfirmDialogInput,
    ConfirmDialogResult,
} from '../../../../projects/kiene-core/src/lib/components/dialogs/confirm-dialog/confirm-dialog';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import {
    FilterItem,
    FilterItemType,
} from '../../../../projects/kiene-core/src/lib/services/backend-api/classes/filter-item';
import { Router } from '@angular/router';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { GutschriftInvoiceKiService } from './gutschrift-invoice-review/gutschrift-invoice-ki.service';
import { GutschriftInvoiceReviewDialogComponent } from './gutschrift-invoice-review/gutschrift-invoice-review-dialog.component';
import { GemeinkostenBulkUploadDialogComponent } from '../gemeinkosten/gemeinkosten-bulk-upload-dialog/gemeinkosten-bulk-upload-dialog.component';
import { NebenkostenPrimanotaVorschauDialogComponent } from '../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-primanota-vorschau-dialog.component';
import { PrimanotaExportVorschauData } from '../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-primanota-vorschau-dialog.models';
import { catchError, forkJoin, of } from 'rxjs';

@Component({
    selector: 'kiene-gutschriften',
    templateUrl: './gutschriften.component.html',
    styleUrls: ['./gutschriften.component.scss'],
})
export class GutschriftenComponent implements OnInit {
    @ViewChild('invoiceKiFileInput') invoiceKiFileInput: ElementRef<HTMLInputElement>;

    tableDescriptor: TableDescriptor = new TableDescriptor();

    constructor(
        public dialog: MatDialog,
        private api: KieneTableService,
        private messageService: MessageService,
        private localStorageService: LocalStorageService,
        private pdfService: PdfViewerDesktopService,
        private fileUploadService: FileUploadService,
        private http: HttpClient,
        private router: Router,
        private invoiceKi: GutschriftInvoiceKiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {}

    ngOnInit() {
        this.tableDescriptor.headline = 'Gutschriften';
        // eigener Key – Finanzen/Kundengutschriften nutzt ebenfalls „Gutschriften“
        this.tableDescriptor.uniqueIdentifier = 'WawiGutschriften';
        this.tableDescriptor.alternativeApiUrl = this.apiBaseUrl + 'gutschrift/read_lieferanten.php';
        this.tableDescriptor.route = '/wawi/gutschrift/';
        this.tableDescriptor.nameOfIdField = 'gutschrift_id';
        this.tableDescriptor.searchPlaceholder = 'Gutschriften durchsuchen';

        const cols: TableColumn[] = [];
        cols.push(new TableColumn('gutschrift_id', 'ID'));
        if (this.localStorageService.currentUserHasPermissions([4105])) {
            cols.push(new PdfTableColumn('pdf_id', 'PDF', this.showPdf.bind(this)));
        }
        cols.push(new TableColumn('lieferant', 'Lieferant'));
        cols.push(new TableColumn('artikel', 'Artikel'));
        cols.push(new TableColumn('gutschrift_nr', 'Gutschrift Nr.'));
        cols.push(new TableColumn('kommentar', 'Kommentar'));
        cols.push(new NumberTableColumn('betrag', 'Betrag (netto)', '1.2-2'));
        cols.push(new TableColumn('mwstsatz', 'MWST (%)', 'number'));
        cols.push(new TableColumn('datum', 'Datum Gutschrift', 'date'));
        cols.push(new TableColumn('erstellt', 'Erstellt Am', 'datetime'));
        cols.push(
            new KieneBooleanTableColumn('scan_review', 'Scan', {
                clickable: {
                    void_: this.toggleScanReview.bind(this),
                    permissionId: 4102,
                    enabled: [new Condition('primanota_id', Operator.EXISTIERT_NICHT, null)],
                },
            }),
        );
        cols.push(new KieneBooleanTableColumn('primanota_id', 'Primanota', { tooltipFieldname: 'primanota_id' }));
        cols.push(new KieneBooleanTableColumn('gutschrift_id_storno', 'Storniert'));
        cols.push(new KieneBooleanTableColumn('gutschrift_id_storniert', 'Stornobuchung'));
        this.tableDescriptor.columns = cols;
        this.tableDescriptor.skipOptionsTablePadding = true;

        this.tableDescriptor.expandable = true;
        this.tableDescriptor.expandableFieldName = 'positionen';
        this.tableDescriptor.showDetailsExpandable = false;
        this.tableDescriptor.expandableLoad = (row: Gutschrift) => this.loadPositionenForRow(row);
        this.tableDescriptor.expandableSumColumns = ['kosten_netto'];
        this.tableDescriptor.expandableColumns = [
            new TableColumn('artikel_me', 'Artikel'),
            new TableColumn('bezeichnung', 'Bezeichnung'),
            new NumberTableColumn('kosten_netto', 'Netto'),
            new NumberTableColumn('mwstsatz', 'MwSt (%)'),
            new TableColumn('buchungsseite', 'S/H'),
            new TableColumn('konto_nr', 'Konto'),
        ];

        this.tableDescriptor.standardCreate = false;
        this.tableDescriptor.showDetails = false;
        this.tableDescriptor.showDetailsVoid = (g: Gutschrift) => {
            if (g?.primanota_id) {
                return;
            }
            if (g?.scan_review > 0 || g?.invoice_draft_id || g?.wawi_run_id) {
                this.openScanReviewDialog(g, g?.scan_review > 0 ? 'review' : 'edit');
                return;
            }
            this.openAddGutschriftDialog(g);
        };

        this.tableDescriptor.permissionView = 4101;
        this.tableDescriptor.permissionCreate = 4102;
        this.tableDescriptor.addVoid = this.openAddGutschriftDialog.bind(this);

        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(
                this.openPrimanotaExportVorschau.bind(this),
                'preview',
                1832,
                'Primanota-Vorschau Alt (Lieferant) vs Neu (Artikel)',
            ),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.generatePrimanota.bind(this), 'account_balance', 1832, 'Primanota erstellen'),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.openInvoiceKiFilePicker.bind(this), 'document_scanner', 17002, 'Gutschrift scannen'),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(
                this.openInvoiceKiBulkPicker.bind(this),
                'library_add',
                17002,
                'Gutschriften Bulk (Scan-Warteschlange)',
            ),
        );
        this.tableDescriptor.toolbarButtons.push(
            new ToolbarButton(this.showKiBulkErrors.bind(this), 'error', 17002, 'Scan-Bulk Fehler'),
        );

        this.tableDescriptor.options.push(
            new TableOption(this.openScanReviewDialog.bind(this), 'Review', 'rate_review', 4102, {
                conditions: [new Condition('scan_review', Operator.GROESSER, 0)],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.openScanReviewDialog.bind(this), 'Nochmal reviewen', 'replay', 4102, {
                conditions: [
                    new Condition('scan_review', Operator.KLEINER_GLEICH, 0),
                    new Condition('invoice_draft_id', Operator.GROESSER, 0),
                ],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.openAddGutschriftDialog.bind(this), 'editieren', 'edit', 4102, {
                conditions: [
                    new Condition('primanota_id', Operator.EXISTIERT_NICHT, null),
                    new Condition('gutschrift_id_storno', Operator.EXISTIERT_NICHT, null),
                    new Condition('gutschrift_id_storniert', Operator.EXISTIERT_NICHT, null),
                    new Condition('scan_review', Operator.KLEINER_GLEICH, 0),
                ],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.stornieren.bind(this), 'stornieren', 'cancel', 4106, {
                conditions: [
                    new Condition('primanota_id', Operator.EXISTIERT, null),
                    new Condition('gutschrift_id_storno', Operator.EXISTIERT_NICHT, null),
                ],
            }),
        );
        this.tableDescriptor.options.push(
            new TableOption(this.openDeleteDialog.bind(this), 'löschen', 'delete', 4104, {
                conditions: [new Condition('primanota_id', Operator.EXISTIERT_NICHT, null)],
            }),
        );

        this.tableDescriptor.filterItems.push(
            new FilterItem('Ohne Primanota', null, FilterItemType.CHECKBOX, 'primanota_id'),
        );
        this.tableDescriptor.filterItems.push(
            new FilterItem('Scan-Review', '1', FilterItemType.CHECKBOX, 'scan_review', true),
        );

        this.tableDescriptor.initialSortColumn = 'erstellt';
        this.tableDescriptor.initialSortDirection = 'desc';

        this.fileUploadService.watchFileUploaded().subscribe(result => {
            if (result) {
                this.api.reload();
            }
        });
    }

    /** Flaches Gutschrift-Schema → eine Expand-Zeile analog Kosten-Position. */
    private loadPositionenForRow(row: Gutschrift) {
        const r = row as Gutschrift & { __expandLoaded?: boolean; positionen?: any[]; konto_nr?: string };
        if (r.__expandLoaded && Array.isArray(r.positionen)) {
            return of(r.positionen);
        }
        r.positionen = (Array.isArray(r.preise) && r.preise.length > 0 ? r.preise : [{ kosten_netto: r.betrag, mwstsatz: r.mwstsatz }]).map(
            (p: { kosten_netto?: number; mwstsatz?: number }) => ({
                artikel_me: r.artikel || '',
                bezeichnung: r.gutschrift_nr || '',
                kosten_netto: Number(p.kosten_netto) || 0,
                mwstsatz: Number(p.mwstsatz) || 0,
                buchungsseite: 'H',
                konto_nr: r.konto_nr || '',
            }),
        );
        r.__expandLoaded = true;
        return of(r.positionen);
    }

    showPdf(g: Gutschrift) {
        const input = new PdfViewerDialogInput(
            'gutschrift/read_pdf.php',
            new HttpParams().set('gutschrift_id', g?.gutschrift_id?.toString()),
            'Gutschrift von ' + g.lieferant,
        );
        this.pdfService.openPdfViewer(input);
    }

    openPrimanotaExportVorschau() {
        this.http
            .post<PrimanotaExportVorschauData>(this.apiBaseUrl + 'primanota/vorschau_lieferanten.php', {
                gutschriften: 1,
            })
            .subscribe({
                next: data => {
                    this.dialog.open(NebenkostenPrimanotaVorschauDialogComponent, {
                        width: '920px',
                        maxWidth: '96vw',
                        data,
                    });
                },
                error: err => {
                    this.messageService.errorMessage('Primanota-Vorschau fehlgeschlagen.', err);
                },
            });
    }

    generatePrimanota() {
        const input = new ConfirmDialogInput();
        input.headline = 'Primanota erstellen';
        input.text =
            'Soll die Primanota für alle offenen Positionen im Reiter "Gutschriften" erstellt werden? Es wird noch die bisherige Lieferanten-Buchung (Alt) erzeugt. Die Artikel-Buchung (Neu) steht in der Primanota-Vorschau.';
        input.okButton = 'Erstellen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { width: '50%', minWidth: '300px', data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.http
                        .post(this.apiBaseUrl + 'primanota/generieren_lieferanten.php', { gutschriften: 1 })
                        .subscribe({
                            next: () => {
                                this.messageService.infoMessage('Die Primanota wurde erfolgreich generiert!');
                                this.router.navigate(['/finanzen', 'primanota']);
                            },
                            error: err => {
                                this.messageService.errorMessage(
                                    'Es ist ein Fehler beim Generieren der Primanota aufgetreten!',
                                    err,
                                );
                            },
                        });
                }
            },
        });
    }

    openInvoiceKiFilePicker() {
        const input = this.invoiceKiFileInput?.nativeElement;
        if (!input) {
            return;
        }
        input.value = '';
        input.click();
    }

    openInvoiceKiBulkPicker() {
        const dialogRef = this.dialog.open(GemeinkostenBulkUploadDialogComponent, {
            width: '560px',
            maxWidth: '95vw',
            autoFocus: false,
        });
        dialogRef.afterClosed().subscribe((files: File[] | undefined) => {
            if (files?.length) {
                this.startInvoiceKiBulk(files);
            }
        });
    }

    onInvoiceKiFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const selected = input.files ? Array.from(input.files) : [];
        input.value = '';
        if (selected.length === 0) {
            return;
        }
        if (selected.length === 1) {
            this.startInvoiceKi(selected[0]);
            return;
        }
        this.startInvoiceKiBulk(selected);
    }

    showKiBulkErrors() {
        this.invoiceKi.bulkFailed(40).subscribe({
            next: res => {
                const jobs = res.jobs ?? [];
                if (jobs.length === 0) {
                    this.messageService.infoMessage('Keine fehlgeschlagenen Scan-Bulk-Jobs.');
                    return;
                }
                const lines = jobs.map(j => `• ${j.fileName}${j.error ? ': ' + j.error : ''}`).join('\n');
                const input = new ConfirmDialogInput();
                input.headline = `Scan-Bulk Fehler (${jobs.length})`;
                input.text = lines;
                input.okButton = 'OK';
                input.showCancel = false;
                this.dialog.open(ConfirmDialogComponent, { data: input, width: '640px' });
            },
            error: err => {
                this.messageService.errorMessage('KI-Fehlerliste konnte nicht geladen werden.', err);
            },
        });
    }

    private startInvoiceKi(file: File) {
        this.messageService.infoMessage('Gutschrift wird analysiert und angelegt…');
        this.invoiceKi.preview(file).subscribe({
            next: draft => {
                const runId = draft?.runId;
                if (!runId) {
                    this.messageService.infoMessage('Keine runId vom Preview.');
                    return;
                }
                this.invoiceKi.autoCommit(runId).subscribe({
                    next: res => {
                        const extra =
                            draft.splitCount && draft.splitCount > 1
                                ? ` (Sammel-PDF: ${draft.splitCount} Belege – restliche werden nachgezogen)`
                                : '';
                        this.messageService.infoMessage(
                            res?.gutschriftId
                                ? `Gutschrift angelegt (ID ${res.gutschriftId})${extra} – Filter „Scan-Review“.`
                                : `Gutschrift angelegt${extra} – Filter „Scan-Review“.`,
                        );
                        this.api.reload();
                        if (draft.splitCount && draft.splitCount > 1 && draft.batchId) {
                            this.pollBulkBatch(draft.batchId);
                        }
                    },
                    error: err => {
                        this.messageService.errorMessage('Auto-Anlage fehlgeschlagen.', err);
                    },
                });
            },
            error: err => {
                this.messageService.errorMessage('Gutschrift-Analyse fehlgeschlagen.', err);
            },
        });
    }

    private startInvoiceKiBulk(files: File[]) {
        this.messageService.infoMessage(`${files.length} Datei(en) in Warteschlange…`);
        this.invoiceKi.bulkEnqueue(files).subscribe({
            next: res => {
                this.messageService.infoMessage(`Bulk gestartet (${res.jobCount} Dateien).`);
                this.pollBulkBatch(res.batchId);
            },
            error: err => {
                this.messageService.errorMessage('Bulk-Upload fehlgeschlagen.', err);
            },
        });
    }

    private pollBulkBatch(batchId: string) {
        const tick = () => {
            this.invoiceKi.bulkStatus(batchId).subscribe({
                next: status => {
                    const pending = (status.totals?.queued ?? 0) + (status.totals?.processing ?? 0);
                    if (pending > 0) {
                        setTimeout(tick, 3000);
                        return;
                    }
                    const ready = (status.jobs ?? []).filter(j => j.status === 'readyForReview' && !!j.runId);
                    if (ready.length === 0) {
                        this.api.reload();
                        return;
                    }
                    forkJoin(
                        ready.map(j =>
                            this.invoiceKi.autoCommit(j.runId!).pipe(
                                catchError(() => of(null)),
                            ),
                        ),
                    ).subscribe({
                        next: () => this.api.reload(),
                        error: () => this.api.reload(),
                    });
                },
                error: () => this.api.reload(),
            });
        };
        setTimeout(tick, 2000);
    }

    toggleScanReview(g: Gutschrift) {
        if (g.primanota_id) {
            return;
        }
        g.scan_review = g.scan_review > 0 ? 0 : 1;
        const fd = new FormData();
        fd.append('gutschrift_id', String(g.gutschrift_id));
        fd.append('lieferant_id', String(g.lieferant_id));
        fd.append('betrag', String(g.betrag ?? 0));
        fd.append('mwstsatz', String(g.mwstsatz ?? 19));
        fd.append('gutschrift_nr', String(g.gutschrift_nr ?? ''));
        fd.append('kommentar', String(g.kommentar ?? ''));
        fd.append('datum', new Date(g.datum).toISOString());
        fd.append('scan_review', String(g.scan_review));
        this.http.post(this.apiBaseUrl + 'gutschrift/update.php', fd).subscribe({
            next: () => this.api.reload(),
            error: err => {
                this.messageService.errorMessage('Scan-Flag konnte nicht gesetzt werden.', err);
                this.api.reload();
            },
        });
    }

    openScanReviewDialog(g?: Gutschrift, mode: 'review' | 'edit' = 'review') {
        if (!g || g.primanota_id) {
            return;
        }
        const runId = String(g.invoice_draft_id || g.wawi_run_id || '');
        const open = (draft: any, pdfFile: File | null) => {
            this.dialog
                .open(GutschriftInvoiceReviewDialogComponent, {
                    width: '100vw',
                    height: '100vh',
                    maxWidth: '100vw',
                    panelClass: 'gemeinkosten-scan-review-dialog-panel',
                    data: { draft, pdfFile, gutschrift: g, mode },
                })
                .afterClosed()
                .subscribe(ok => {
                    if (ok) {
                        this.api.reload();
                    }
                });
        };

        const stubDraft = {
            runId: runId || '',
            mandantId: 0,
            benutzerId: 0,
            suggestedRechnungNr: g.gutschrift_nr,
            suggestedLieferantId: g.lieferant_id,
            suggestedLieferantName: g.lieferant,
            suggestedDatum: g.datum,
            kommentar: g.kommentar,
            preise:
                Array.isArray(g.preise) && g.preise.length > 0
                    ? g.preise.map(p => ({ kostenNetto: p.kosten_netto, mwstsatz: p.mwstsatz }))
                    : [{ kostenNetto: g.betrag, mwstsatz: g.mwstsatz }],
        };

        if (runId) {
            forkJoin({
                draft: this.invoiceKi.getDraft(runId).pipe(catchError(() => of(null))),
                pdf: this.invoiceKi.getPdfFile(runId).pipe(
                    catchError(() =>
                        g.pdf_id
                            ? this.invoiceKi.getGutschriftPdfFile(g.gutschrift_id).pipe(catchError(() => of(null)))
                            : of(null),
                    ),
                ),
            }).subscribe({
                next: ({ draft, pdf }) => open(draft ?? stubDraft, pdf),
            });
            return;
        }

        if (g.pdf_id) {
            this.invoiceKi.getGutschriftPdfFile(g.gutschrift_id).subscribe({
                next: pdf => open(stubDraft, pdf),
                error: () => this.openAddGutschriftDialog(g),
            });
            return;
        }

        this.openAddGutschriftDialog(g);
    }

    openAddGutschriftDialog(g?: Gutschrift) {
        if (!g?.primanota_id && !g?.gutschrift_id_storniert && !g?.gutschrift_id_storno) {
            const dialogRef = this.dialog.open(AddGutschriftDialogComponent, {
                width: '560px',
                data: { gutschrift: g, apiUrl: 'gutschrift/update.php' },
            });

            dialogRef.afterClosed().subscribe(() => {
                this.api.reload();
            });
        }
    }

    stornieren(g: Gutschrift) {
        const input = new ConfirmDialogInput();
        input.text = 'Soll diese Gutschrift wirklich storniert werden?';
        input.headline = 'Stornieren';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { width: '500px', data: input });

        dialogRef.afterClosed().subscribe({
            next: (response: ConfirmDialogResult) => {
                if (response?.ok && response?.confirmed) {
                    this.api.post(this.apiBaseUrl + 'gutschrift/stornieren.php', g).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Gutschrift erfolgreich storniert!');
                            this.api.reload();
                        },
                        error: err => {
                            this.messageService.errorMessage('Gutschrift konnte nicht storniert werden!', err);
                        },
                    });
                }
            },
        });
    }

    openDeleteDialog(g: Gutschrift) {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            width: '500px',
            data: {
                title: 'Gutschrift wirklich löschen?',
                text: 'Gutschrift von ' + g.lieferant + ' wirklich löschen?',
                textButton: 'löschen',
            },
        });

        dialogRef.afterClosed().subscribe(response => {
            if (response) {
                this.api.deleteEntity(this.apiBaseUrl + 'gutschrift/delete.php', g).subscribe(
                    () => {
                        this.messageService.infoMessage('Die Gutschrift wurde erfolgreich gelöscht!');
                        this.api.reload();
                    },
                    error => {
                        this.messageService.errorMessage('Die Gutschrift konnte nicht gelöscht werden! ', error);
                    },
                );
            }
        });
    }
}
